import ArrowBackIcon from "@mui/icons-material/KeyboardArrowLeft";
import ArrowForwardIcon from "@mui/icons-material/KeyboardArrowRight";
import { Box, IconButton, Theme, useTheme } from "@mui/material";
import { Dayjs } from "dayjs";
import { getToday } from "src/lib/date.helper";

interface Props {
  currentMonth: Dayjs;
  onMonthChange: (newMonth: Dayjs) => void;
  onViewChange: (view: "day" | "month" | "year") => void;
  variant: "day" | "week" | "month" | "year";
  minDate: Dayjs;
  maxDate: Dayjs;
  disableFuture?: boolean;
}

const getStyles = (theme: Theme) => ({
  header: {
    display: "flex",
    justifyContent: "center",
    alignItems: "center",
    width: "100%",
    paddingY: 1,
    gap: 1,
  },

  selectorBox: {
    display: "flex",
    alignItems: "center",
    gap: 0.5,
    paddingX: 1,
    paddingY: 0.5,
    cursor: "pointer",
    borderBottom: "1px solid transparent",
    "&:hover": { background: theme.palette.surface.overlay },
  },
});

export default function PickerHeader({
  currentMonth,
  onMonthChange,
  onViewChange,
  variant,
  minDate,
  maxDate,
  disableFuture = false,
}: Props) {
  const theme = useTheme();
  const styles = getStyles(theme);

  const showMonthControls = variant === "day" || variant === "week";
  const today = getToday();

  const prevMonth = currentMonth.subtract(1, "month");
  const nextMonth = currentMonth.add(1, "month");
  const prevYear = currentMonth.subtract(1, "year");
  const nextYear = currentMonth.add(1, "year");

  const isPrevMonthDisabled = prevMonth.endOf("month").isBefore(minDate);
  const isNextMonthDisabled =
    nextMonth.startOf("month").isAfter(maxDate) ||
    (disableFuture && nextMonth.startOf("month").isAfter(today));

  const isPrevYearDisabled = prevYear.endOf("year").isBefore(minDate);
  const isNextYearDisabled =
    nextYear.startOf("year").isAfter(maxDate) ||
    (disableFuture && nextYear.startOf("year").isAfter(today));

  return (
    <Box sx={styles.header}>
      {showMonthControls && (
        <>
          <IconButton onClick={() => onMonthChange(prevMonth)} disabled={isPrevMonthDisabled}>
            <ArrowBackIcon />
          </IconButton>

          <Box sx={styles.selectorBox} onClick={() => onViewChange("month")}>
            {currentMonth.format("MMM")}
          </Box>

          <IconButton onClick={() => onMonthChange(nextMonth)} disabled={isNextMonthDisabled}>
            <ArrowForwardIcon />
          </IconButton>
        </>
      )}

      <>
        <IconButton onClick={() => onMonthChange(prevYear)} disabled={isPrevYearDisabled}>
          <ArrowBackIcon />
        </IconButton>

        <Box sx={styles.selectorBox} onClick={() => onViewChange("year")}>
          {currentMonth.year()}
        </Box>

        <IconButton onClick={() => onMonthChange(nextYear)} disabled={isNextYearDisabled}>
          <ArrowForwardIcon />
        </IconButton>
      </>
    </Box>
  );
}